/**
 * Auto-pick a quality tier based on device hints. Cheaper than
 * runtime FPS sampling and gives a stable experience from frame 0.
 *
 * - low:    no antialias, 1024 shadow map, basic shadows, pixelRatio = 1
 * - medium: AA, 1024 shadow map, soft shadows, pixelRatio capped at 1.5
 * - high:   AA, 2048 shadow map, soft shadows, pixelRatio capped at 2
 */
export type Quality = "low" | "medium" | "high";

export interface QualitySettings {
  antialias: boolean;
  shadowMapSize: number;
  softShadows: boolean;
  pixelRatioCap: number;
  hemiLightIntensity: number;
  /** When false, skip the shadow-map pass entirely. The single biggest
   *  GPU saving on older tablets. */
  enableShadows: boolean;
  /** When false, skip the PMREM RoomEnvironment pre-bake + scene
   *  `environment` assignment. Materials fall back to plain hemisphere
   *  + sun lighting. */
  enableEnvMap: boolean;
  /** When false, skip the per-material "Fall Guys plastic" tweak pass
   *  (roughness/metalness adjust + envMapIntensity bump). */
  enablePlasticPass: boolean;
  /** When false, skip decorative particle / atmosphere layers
   *  (snowfall, asteroids, decor decor — anything that adds draw calls
   *  but isn't gameplay). */
  enableAmbientDecor: boolean;
  /** When false, skip the scene fog. Cheap to render but reduces
   *  alpha overdraw. */
  enableFog: boolean;
}

const TIERS: Record<Quality, QualitySettings> = {
  // "Low" — true bare-bones for old iPads. Shadows OFF, env-map OFF,
  // plastic pass OFF, decor OFF, fog OFF, pixel ratio under-sampled.
  // Geometry + colours still render so the game is fully playable;
  // it just loses all of the polish layers.
  low: {
    antialias: false,
    shadowMapSize: 0,
    softShadows: false,
    pixelRatioCap: 0.75,
    hemiLightIntensity: 0.55,
    enableShadows: false,
    enableEnvMap: false,
    enablePlasticPass: false,
    enableAmbientDecor: false,
    enableFog: false,
  },
  medium: {
    antialias: true,
    shadowMapSize: 2048,
    softShadows: true,
    pixelRatioCap: 1.5,
    hemiLightIntensity: 0.6,
    enableShadows: true,
    enableEnvMap: true,
    enablePlasticPass: true,
    enableAmbientDecor: true,
    enableFog: true,
  },
  high: {
    antialias: true,
    shadowMapSize: 4096,
    softShadows: true,
    pixelRatioCap: 2,
    hemiLightIntensity: 0.55,
    enableShadows: true,
    enableEnvMap: true,
    enablePlasticPass: true,
    enableAmbientDecor: true,
    enableFog: true,
  },
};

let cached: Quality | null = null;
const STORAGE_KEY = "wr_quality";

/** Read the real GPU renderer string (the single best signal for GPU power).
 *  Cheap Android tablets report 8 weak cores + a deviceMemory fallback, so
 *  cores/memory over-estimate them — the GPU string does not. */
function readGpuRenderer(): string {
  try {
    const c = document.createElement("canvas");
    const gl = (c.getContext("webgl") || c.getContext("experimental-webgl")) as WebGLRenderingContext | null;
    if (!gl) return "";
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    const r = ext ? gl.getParameter((ext as { UNMASKED_RENDERER_WEBGL: number }).UNMASKED_RENDERER_WEBGL) : "";
    return String(r || "").toLowerCase();
  } catch { return ""; }
}

// Weak mobile GPUs (incl. the TCL Tab 10L's PowerVR GE8320) → force low.
const WEAK_GPU = /powervr|mali-4|mali-t|mali-g31|mali-g51|mali-g52|mali-g57|adreno \(tm\) [345]0|adreno \(tm\) 3\d\d|swiftshader|software|llvmpipe|apple gpu \(low/;

export function detectQuality(): Quality {
  if (cached) return cached;
  // Manual override (Settings → Performance mode). Wins over everything.
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as Quality | null;
    if (saved && saved in TIERS) {
      cached = saved;
      return cached;
    }
  } catch {}
  // Strongest signal first: a known-weak GPU pins us to low regardless of
  // core count / reported memory.
  const gpu = readGpuRenderer();
  if (gpu && WEAK_GPU.test(gpu)) { cached = "low"; return cached; }

  const dpr = window.devicePixelRatio || 1;
  const cores = (navigator as { hardwareConcurrency?: number }).hardwareConcurrency ?? 4;
  const memGB =
    (navigator as { deviceMemory?: number }).deviceMemory ??
    (cores >= 8 ? 8 : 4);
  const android = /Android/i.test(navigator.userAgent);
  const mobile = android || /iPhone|iPad|iPod/i.test(navigator.userAgent);
  let score = 0;
  if (cores >= 8) score += 2;
  else if (cores >= 4) score += 1;
  if (memGB >= 8) score += 2;
  else if (memGB >= 4) score += 1;
  if (dpr >= 2) score += 1;
  if (mobile) score -= 2;

  let q: Quality = score >= 4 ? "high" : score >= 2 ? "medium" : "low";
  // Android GPUs are weak even on many-core SoCs — never pick "high", and if
  // we don't have a real GPU signal, err toward "low".
  if (android) {
    if (q === "high") q = "medium";
    if (!gpu) q = "low";
  }
  cached = q;
  return cached;
}

/** True when we're on the bare-bones tier — used to skip the heavy non-game
 *  3D surfaces (menu hero, avatar viewer, level thumbnails) entirely. */
export function isLowQuality(): boolean {
  return detectQuality() === "low";
}

/** Clear a manual override so detection runs fresh (Settings → "Auto"). */
export function clearQualityOverride() {
  cached = null;
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

export function setQuality(q: Quality) {
  cached = q;
  try {
    localStorage.setItem(STORAGE_KEY, q);
  } catch {}
}

export function getQualitySettings(q: Quality = detectQuality()): QualitySettings {
  return TIERS[q];
}
